import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { Typography, Paper, CircularProgress, Box, Button } from '@mui/material';
import API from '@utils/axios';
import Layout from '@components/Layout';
import PageError from '@components/PageError';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

function ButtonPage() {
    const { t } = useTranslation();
    const { key } = useParams();
    const [searchParams] = useSearchParams();
    const action = searchParams.get('action');

    const [loading, setLoading] = useState(true);
    const [errorCode, setErrorCode] = useState(null);
    const [actionData, setActionData] = useState(null);
    const [confirmed, setConfirmed] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);

    useEffect(() => {
        const fetchAction = async () => {
            try {
                const { data } = await API.get(`/actions/${key}`);
                if (data.actionType !== 'contact-button') {
                    setErrorCode('invalid_action_type');
                    return;
                }
                if (!data.actions?.includes(action)) {
                    setErrorCode('invalid_action_parameter');
                    return;
                }
                setActionData(data);
            } catch (err) {
                if (err.response?.status === 404) {
                    setErrorCode(err.response.data.code);
                } else {
                    setErrorCode('internal_error');
                    console.error('Error:', err);
                }
            } finally {
                setLoading(false);
            }
        };

        if (key) {
            fetchAction();
        }
    }, [key, action]);

    const handleConfirm = async () => {
        setConfirmLoading(true);
        try {
            const response = await API.post(`/actions/${key}`, action, {
                headers: {
                    'Content-Type': 'text/plain'
                }
            });
            if (response.status === 200 && response.data.success) {
                setConfirmed(true);
            } else {
                setErrorCode('confirm_error');
            }
        } catch (err) {
            console.error('Error:', err);
            setErrorCode('confirm_internal_error');
        } finally {
            setConfirmLoading(false);
        }
    };

    return errorCode ? (
        <PageError
            title={t('Page not found')}
            message={t('This link is invalid or expired.')}
            message2={t('Error code: {{code}}', { code: errorCode })}
        />
    ) : (
        loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: '20vh' }} >
                <CircularProgress />
            </Box >
        ) : (
            confirmed ? (
                <Layout customer={actionData.customer} size="sm" title={t('Choice confirmed')} >
                    <Paper sx={{ px: 3, py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="h2">
                            {t('Choice confirmed')}
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2, mb: 5 }}>
                            {t('Your choice has been confirmed. Thank you for your action.')}
                        </Typography>
                        <Button variant="outlined" color="success"
                            size="large" disabled
                            startIcon={<CheckCircleIcon />}
                            sx={{ 
                                "&.Mui-disabled": {
                                    backgroundColor: "success.main",
                                    color: "white"
                                }
                            }}
                        >
                            {action}
                        </Button>
                    </Paper>
                </Layout >
            ) : (
                <Layout customer={actionData.customer} size="sm" title={t('Confirm your choice')} >
                    <Paper sx={{ px: 3, py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="h2">
                            {t('Confirm your choice')}
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2, mb: 5 }}>
                            {t('Please confirm your choice by clicking on the button below.')}
                        </Typography>
                        <Button 
                            variant="contained" 
                            color="primary"
                            size="large"
                            onClick={handleConfirm}
                            disabled={confirmLoading}
                            startIcon={confirmLoading && <CircularProgress size={20} color="inherit" />}
                        >
                            {action}
                        </Button>
                    </Paper>
                </Layout >
            )
        )
    );
}

export default ButtonPage;
